import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import { useToastStore } from '../../../store/toast.store';
import * as notificationService from '../services/notifications.service';
import type {
  AdminNotificationDTO,
  NotificationCategory,
  UpsertAdminNotificationRequest,
} from '../types/notifications.types';

function resolveError(error: unknown): string {
  if (!isApiError(error)) {
    return '未知错误';
  }

  const map: Record<string, string> = {
    unauthorized: '登录状态已失效，请重新登录',
    admin_role_required: '无管理员权限',
    'invalid notification category': '消息分类不合法',
    'notification title is required': '标题不能为空',
    'notification summary is required': '摘要不能为空',
    'notification body is required': '正文不能为空',
    'notification not found': '该消息已不存在',
    network_error: '网络连接失败，请检查网络',
  };

  return map[error.message] ?? `请求失败 (${error.message})`;
}

export function useNotificationList() {
  const addToast = useToastStore((s) => s.addToast);
  const [items, setItems] = useState<AdminNotificationDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [category, setCategoryRaw] = useState<'' | NotificationCategory>('');
  const [marketId, setMarketIdRaw] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (targetPage: number, targetCategory: '' | NotificationCategory) => {
      setLoading(true);
      setError(null);
      try {
        const result = await notificationService.listNotifications({
          page: targetPage,
          pageSize,
          marketId,
          category: targetCategory,
        });
        setItems(result.items ?? []);
        setTotal(result.total);
        setPage(result.page);
      } catch (e) {
        setError(resolveError(e));
      } finally {
        setLoading(false);
      }
    },
    [marketId, pageSize],
  );

  useEffect(() => {
    void load(page, category);
  }, [category, load, page]);

  const setCategory = useCallback((value: '' | NotificationCategory) => {
    setCategoryRaw(value);
    setPage(1);
  }, []);

  const setMarketId = useCallback((value: string) => {
    setMarketIdRaw(value);
    setPage(1);
  }, []);

  const reload = useCallback(async () => {
    await load(page, category);
  }, [category, load, page]);

  const create = useCallback(
    async (payload: UpsertAdminNotificationRequest) => {
      setSaving(true);
      try {
        await notificationService.createNotification(payload);
        addToast('success', '消息通知已创建');
        await load(1, category);
        setPage(1);
        return true;
      } catch (e) {
        addToast('error', resolveError(e));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [addToast, category, load],
  );

  const update = useCallback(
    async (id: number, payload: UpsertAdminNotificationRequest) => {
      setSaving(true);
      try {
        await notificationService.updateNotification(id, payload);
        addToast('success', '消息通知已更新');
        await load(page, category);
        return true;
      } catch (e) {
        addToast('error', resolveError(e));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [addToast, category, load, page],
  );

  const remove = useCallback(
    async (id: number) => {
      setSaving(true);
      try {
        await notificationService.deleteNotification(id);
        addToast('success', '消息通知已删除');
        const nextPage = items.length === 1 && page > 1 ? page - 1 : page;
        await load(nextPage, category);
        setPage(nextPage);
        return true;
      } catch (e) {
        addToast('error', resolveError(e));
        return false;
      } finally {
        setSaving(false);
      }
    },
    [addToast, category, items.length, load, page],
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return {
    items,
    total,
    page,
    pageSize,
    totalPages,
    category,
    marketId,
    loading,
    saving,
    error,
    setPage,
    setCategory,
    setMarketId,
    reload,
    create,
    update,
    remove,
  };
}
